import type { MediaAsset } from "./types";

export const MEDIA_CACHE_NAME = "avante-media-v1";
export const WAVEFORM_DB_NAME = "avante-waveforms-v1";

const WAVEFORM_STORE = "waveforms";
const MEDIA_STORE = "media";
const CACHE_OWNER_KEY = "avante-media-cache-owner-v1";
const WAVEFORM_SCHEMA = 1;
const MAX_MEDIA_ENTRIES = 10;
const MAX_MEDIA_BYTES = 500 * 1024 * 1024;

export interface CachedWaveform {
  key: string;
  peaks: number[];
  duration: number;
  bars: number;
  schema: number;
  touchedAt: number;
}

interface MediaCacheEntry {
  key: string;
  url: string;
  bytes: number;
  touchedAt: number;
}

const browserIndexedDb = () =>
  typeof indexedDB === "undefined" ? undefined : indexedDB;

const browserCaches = () =>
  typeof caches === "undefined" ? undefined : caches;

const mediaVersion = (media: MediaAsset) =>
  [media.id, media.objectKey, media.size, media.mime, media.createdAt].join(
    ":",
  );

export const cacheableAudioUrl = (media: MediaAsset) =>
  `/api/media/${encodeURIComponent(media.id)}?kind=audio&v=${encodeURIComponent(
    mediaVersion(media),
  )}`;

export const mediaCacheKey = (media: MediaAsset) =>
  `media:${mediaVersion(media)}`;

export const waveformCacheKey = (media: MediaAsset, bars: number) =>
  `peaks:${mediaVersion(media)}:bars:${bars}:schema:${WAVEFORM_SCHEMA}`;

const requestResult = <T>(request: IDBRequest<T>) =>
  new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const transactionDone = (transaction: IDBTransaction) =>
  new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });

const openDb = async () => {
  const idb = browserIndexedDb();
  if (!idb) return undefined;
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = idb.open(WAVEFORM_DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(WAVEFORM_STORE))
        db.createObjectStore(WAVEFORM_STORE, { keyPath: "key" });
      if (!db.objectStoreNames.contains(MEDIA_STORE))
        db.createObjectStore(MEDIA_STORE, { keyPath: "key" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const readStore = async <T>(storeName: string, key: string) => {
  const db = await openDb();
  if (!db) return undefined;
  try {
    const transaction = db.transaction(storeName, "readonly");
    return (await requestResult(
      transaction.objectStore(storeName).get(key),
    )) as T | undefined;
  } finally {
    db.close();
  }
};

const writeStore = async <T extends { key: string }>(
  storeName: string,
  value: T,
) => {
  const db = await openDb();
  if (!db) return;
  try {
    const transaction = db.transaction(storeName, "readwrite");
    transaction.objectStore(storeName).put(value);
    await transactionDone(transaction);
  } finally {
    db.close();
  }
};

const clearStore = async (storeName: string) => {
  const db = await openDb();
  if (!db) return;
  try {
    const transaction = db.transaction(storeName, "readwrite");
    transaction.objectStore(storeName).clear();
    await transactionDone(transaction);
  } finally {
    db.close();
  }
};

const deleteStoreEntry = async (storeName: string, key: string) => {
  const db = await openDb();
  if (!db) return;
  try {
    const transaction = db.transaction(storeName, "readwrite");
    transaction.objectStore(storeName).delete(key);
    await transactionDone(transaction);
  } finally {
    db.close();
  }
};

const listMediaEntries = async () => {
  const db = await openDb();
  if (!db) return [];
  try {
    const transaction = db.transaction(MEDIA_STORE, "readonly");
    return (await requestResult(
      transaction.objectStore(MEDIA_STORE).getAll(),
    )) as MediaCacheEntry[];
  } finally {
    db.close();
  }
};

export const getCachedWaveform = async (key: string) => {
  try {
    const entry = await readStore<CachedWaveform>(WAVEFORM_STORE, key);
    if (!entry || entry.schema !== WAVEFORM_SCHEMA) return undefined;
    void writeStore(WAVEFORM_STORE, { ...entry, touchedAt: Date.now() });
    return entry;
  } catch {
    return undefined;
  }
};

export const saveCachedWaveform = async (
  key: string,
  peaks: number[],
  duration: number,
  bars: number,
) => {
  try {
    await writeStore<CachedWaveform>(WAVEFORM_STORE, {
      key,
      peaks,
      duration,
      bars,
      schema: WAVEFORM_SCHEMA,
      touchedAt: Date.now(),
    });
  } catch {
    // Waveform caching is a performance hint. Playback must keep working.
  }
};

const deleteCachedMedia = async (entry: MediaCacheEntry) => {
  await browserCaches()
    ?.open(MEDIA_CACHE_NAME)
    .then((cache) => cache.delete(entry.url))
    .catch(() => undefined);
  await deleteStoreEntry(MEDIA_STORE, entry.key).catch(() => undefined);
};

const trimMediaCache = async (force = false) => {
  const entries = (await listMediaEntries()).sort(
    (first, second) => first.touchedAt - second.touchedAt,
  );
  let total = entries.reduce((sum, entry) => sum + entry.bytes, 0);
  while (
    entries.length &&
    (force || entries.length > MAX_MEDIA_ENTRIES || total > MAX_MEDIA_BYTES)
  ) {
    const oldest = entries.shift()!;
    total -= oldest.bytes;
    await deleteCachedMedia(oldest);
    if (force) break;
  }
};

const putMediaResponse = async (
  cache: Cache,
  request: Request,
  response: Response,
  entry: MediaCacheEntry,
) => {
  try {
    await cache.put(request, response.clone());
  } catch {
    await trimMediaCache(true);
    await cache.put(request, response.clone()).catch(() => undefined);
  }
  await writeStore(MEDIA_STORE, entry).catch(() => undefined);
  await trimMediaCache().catch(() => undefined);
};

export const fetchAndCacheAudio = async (media: MediaAsset) => {
  const url = cacheableAudioUrl(media);
  const absoluteUrl = new URL(url, window.location.origin).toString();
  const request = new Request(absoluteUrl, { credentials: "same-origin" });
  const storage = browserCaches();
  if (!storage) {
    const response = await fetch(request);
    if (!response.ok)
      throw new Error(`Media request failed (${response.status})`);
    return response.arrayBuffer();
  }

  const cache = await storage.open(MEDIA_CACHE_NAME);
  const cached = await cache.match(request);
  if (cached) {
    await writeStore<MediaCacheEntry>(MEDIA_STORE, {
      key: mediaCacheKey(media),
      url: absoluteUrl,
      bytes: media.size,
      touchedAt: Date.now(),
    }).catch(() => undefined);
    return cached.arrayBuffer();
  }

  const response = await fetch(request);
  if (!response.ok)
    throw new Error(`Media request failed (${response.status})`);
  const headers = new Headers(response.headers);
  const buffer = await response.arrayBuffer();
  const cacheable = new Response(buffer.slice(0), {
    status: response.status,
    statusText: response.statusText,
    headers,
  });
  await putMediaResponse(cache, request, cacheable, {
    key: mediaCacheKey(media),
    url: absoluteUrl,
    bytes: media.size || buffer.byteLength,
    touchedAt: Date.now(),
  });
  return buffer;
};

export const clearClientMediaCaches = async () => {
  await Promise.all([
    browserCaches()
      ?.keys()
      .then((keys) =>
        Promise.all(
          keys
            .filter((key) => key.startsWith("avante-media-"))
            .map((key) => browserCaches()?.delete(key)),
        ),
      )
      .catch(() => undefined),
    clearStore(WAVEFORM_STORE).catch(() => undefined),
    clearStore(MEDIA_STORE).catch(() => undefined),
  ]);
};

export const ensureClientMediaCacheOwner = async (memberId: string) => {
  try {
    const existing = localStorage.getItem(CACHE_OWNER_KEY);
    if (existing && existing !== memberId) await clearClientMediaCaches();
    localStorage.setItem(CACHE_OWNER_KEY, memberId);
  } catch {
    // Storage ownership tracking is best-effort; server auth stays authoritative.
  }
};
